import { Alert, Select, TabCard, TextField, Toggle, Tooltip } from '@cimpress/react-components';
import { useContext, useEffect, useRef, useState } from 'react';
import { SketchPicker } from 'react-color';
import { getViews } from '@rendering/lookup';
import { ApiKeyContext } from '../App';
import {
    CardBuilder,
    Control,
    Culture,
    Page,
    PreviewRow,
    PreviewRows,
    PreviewSection,
    SurfaceSelector,
    useGenerateParams,
    VortexVisualizationCard,
    TENANT_TYPE_COMBINATIONS,
    CultureV4,
} from '../Components';
import { CustomSelector, type SelectorOption } from '../Components/common';
import { InputJsonSelector } from '../Components/common/InputJsonSelector';
import { getAuthHeaders } from '../Auth';
import {
    getCompositionGenerateUrl,
    getPhotoSceneUrl,
    getProductNameBasedOnSku,
    getProductCurrentVersion,
} from '../api';
import { DEFAULT_PANEL_SELECTOR_OPTION, PRIORITY_OPTIONS } from '../constants';
import { brandProfileUrls } from '../data/generateBrandProfileDocuments';
import { fullbleedSourceDocuments } from '../data/generateFullbleedDocuments';
import predefinedComponents from '../data/generatePrediefinedComponents.json';
import presetSurfaces from '../data/generateSurfaceJson.json';
import presetSurfaceUrls from '../data/generateSurfaceUrls.json';
import prodsAndOptions from '../data/prodandoptions.json';
import {
    isUrl,
    useCancelableEffect,
    lookupSurfaceFps,
    parseOptionsFromSurfaceUrl,
    resolveSurfaceFps,
    resolveProductSku,
} from '../tools';

import classes from './GeneratePage.module.css';

const SKU_REGEX = /^[A-Z]{3}-[A-Z0-9]+$/;
const CALCULATED_SURFACE_SETS_BASE_URL = 'https://surface.products.cimpress.io/v3/calculatedsurfacesets';

const V3_CULTURE_VALUE_OPTIONS = Object.values(Culture).map((culture) => {
    return { label: culture, value: culture };
});
const V4_CULTURE_VALUE_OPTIONS = [
    { label: 'Custom', value: '' },
    ...Object.values(CultureV4).map((culture) => {
        return { label: culture, value: culture };
    }),
];

// Extracts the SKU-like part of the URL
function getSkuFromSurfaceSpecUrl(url: string) {
    const surfaceSpecParts = url.split('?')[0].split('/');
    return surfaceSpecParts.find((x) => SKU_REGEX.test(x));
}

function findFullfillmentInfoForProductKey(key?: string) {
    if (!key) {
        return undefined;
    }
    const match = prodsAndOptions.find((x) => x.PRODUCT_KEY === key);
    return match ? match.FULFILLMENT_SKU : undefined;
}

function convertToCalculatedSurfaceSpecUrl(source: string) {
    const matched = lookupSurfaceFps(source);
    if (matched) {
        if (matched.id.startsWith('PRD-') || !source.includes('calculatedsurfacesets')) {
            return source;
        }
        return `${CALCULATED_SURFACE_SETS_BASE_URL}/${matched.id}?variables=${encodeURIComponent(JSON.stringify(matched.options))}`;
    }

    if (source.startsWith(CALCULATED_SURFACE_SETS_BASE_URL)) {
        return source;
    }
    const originalSku = getSkuFromSurfaceSpecUrl(source);
    if (!originalSku) {
        return source;
    }
    const replacementSku = findFullfillmentInfoForProductKey(originalSku);
    if (!replacementSku) {
        return source;
    }
    // Break down the original
    const parts = source.split('&');
    const optionsPart = parts.find((x) => x.startsWith('optionSelections='));
    if (!optionsPart) {
        return source;
    }
    const variables = optionsPart.replace('optionSelections=', '');
    return `${CALCULATED_SURFACE_SETS_BASE_URL}/${replacementSku}?variables=${variables}`;
}

export const GeneratePage = () => {
    const {
        assets,
        culture,
        dpiThreshold,
        panels,
        priority,
        productName,
        brandProfileUrl,
        sourceDocumentUrl,
        surfaceSpecification,
        surfaceSpecificationUrl,
        substrateColor,
        isVersion4,
        enableMulticolorLogoContrastFix,
        enableMulticolorLogoOutlineContrastFix,
        enableEmbroideryLogoContrastFix,
        enableGeneratePatternDocument,
        setAssets,
        setCulture,
        setDpiThreshold,
        setPanels,
        setPriority,
        setProductName,
        setBrandProfileUrl,
        setSourceDocumentUrl,
        setSurfaceSpecification,
        setSurfaceSpecificationUrl,
        setSubstrateColor,
        setIsVersion4,
        setEnableMulticolorLogoContrastFix,
        setEnableMulticolorLogoOutlineContrastFix,
        setEnableEmbroideryLogoContrastFix,
        setEnableGeneratePatternDocument,
    } = useGenerateParams();
    const [originRow, setOriginRow] = useState(PreviewRow());
    const [resultRow, setResultRow] = useState(PreviewRow());
    const initialProductNameRef = useRef(productName);
    const hasPageLoadedRef = useRef(false);
    const [hasRequiredInput, setHasRequiredInput] = useState(true);
    const [isDocumentAssetAlertInfo, setIsDocumentAssetAlertInfo] = useState(true);
    const apiKey = useContext(ApiKeyContext);
    const [detectedProductCategory, setDetectedProductCategory] = useState('');
    const [show3DPreview, setShow3DPreview] = useState(false);
    const [cultureInput, setCultureInput] = useState(culture.value);
    const cultureChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (cultureChangeTimeoutRef.current !== null) {
                clearTimeout(cultureChangeTimeoutRef.current);
            }
        };
    }, []);

    const panelsValueOptions = [
        { label: 'First', value: 'first' },
        { label: 'All', value: 'all' },
    ];

    useEffect(() => {
        setHasRequiredInput(
            (assets || sourceDocumentUrl || brandProfileUrl) && (surfaceSpecificationUrl || surfaceSpecification),
        );
    }, [assets, sourceDocumentUrl, brandProfileUrl, surfaceSpecificationUrl, surfaceSpecification]);

    useEffect(() => {
        if (hasRequiredInput) {
            setIsDocumentAssetAlertInfo(true);

            if (sourceDocumentUrl) {
                const fullBleedCard = CardBuilder.ofType('documentUrl')
                    .withTitle('Source Document')
                    .withData({ documentUrl: sourceDocumentUrl, backgroundColor: 'FFFFFF' })
                    .build();
                setOriginRow(PreviewRow(fullBleedCard));
            } else {
                setOriginRow(PreviewRow());
            }

            // Calculate URLs once for both previews
            const calculatedSurfaceSpecUrl = surfaceSpecificationUrl
                ? convertToCalculatedSurfaceSpecUrl(surfaceSpecificationUrl)
                : undefined;

            const docUrl = getCompositionGenerateUrl({
                assets,
                surfaceSpecificationUrl,
                sourceDocumentUrl,
                brandProfileUrl,
                surfaceSpecification,
                productName,
                panels: panels.value,
                ...(isVersion4 ? { priority: priority.value } : { dpiThreshold }),
                culture: culture.value,
                substrateColor,
                version: isVersion4 ? 'v4' : 'v3',
                apiKey,
                enableMulticolorLogoContrastFix: isVersion4 && enableMulticolorLogoContrastFix,
                enableMulticolorLogoOutlineContrastFix: isVersion4 && enableMulticolorLogoOutlineContrastFix,
                enableEmbroideryLogoContrastFix: isVersion4 && enableEmbroideryLogoContrastFix,
                enableGeneratePatternDocument,
            });

            const resultCard = CardBuilder.ofType('documentUrl')
                .withTitle('Result Document (2D Preview)')
                .withLoader(async ({ signal }) => {
                    const photoSceneUrl = calculatedSurfaceSpecUrl
                        ? ((await getPhotoSceneUrl(calculatedSurfaceSpecUrl, signal)) ?? '')
                        : undefined;

                    return {
                        documentUrl: docUrl,
                        photoSceneUrl,
                        surfaceUrl: calculatedSurfaceSpecUrl,
                        backgroundColor: substrateColor || 'FFFFFF',
                    };
                })
                .build();
            resultCard.onAgsMetadataLoaded = (metadata) => setDetectedProductCategory(metadata.productCategory ?? '');

            // Create 3D preview card if enabled
            const preview3DCard = show3DPreview
                ? CardBuilder.ofType('preview3d')
                      .withTitle('Result Document (3D Preview)')
                      .withChildComponent(() => (
                          <VortexVisualizationCard documentUrl={docUrl} surfaceUrl={calculatedSurfaceSpecUrl || ''} />
                      ))
                      .build()
                : null;

            setResultRow(preview3DCard ? PreviewRow(resultCard, preview3DCard) : PreviewRow(resultCard));
        } else {
            if (assets && sourceDocumentUrl) {
                setIsDocumentAssetAlertInfo(false);
            }
        }
    }, [
        assets,
        hasRequiredInput,
        surfaceSpecificationUrl,
        sourceDocumentUrl,
        brandProfileUrl,
        productName,
        panels,
        dpiThreshold,
        culture,
        priority,
        surfaceSpecification,
        substrateColor,
        isVersion4,
        enableMulticolorLogoContrastFix,
        enableMulticolorLogoOutlineContrastFix,
        enableEmbroideryLogoContrastFix,
        enableGeneratePatternDocument,
        apiKey,
        show3DPreview,
    ]);

    useCancelableEffect(
        (signal) => {
            (async () => {
                if (!surfaceSpecificationUrl) {
                    setShow3DPreview(false);
                    return;
                }

                const resolved = await resolveSurfaceFps(surfaceSpecificationUrl, signal);
                let mcpSku = resolved?.id;
                let productVersion = resolved?.version ?? null;
                let options = resolved?.options ?? {};

                // 1. Resolve product name if SKU is available
                if (mcpSku || surfaceSpecificationUrl) {
                    const productSku = findFullfillmentInfoForProductKey(mcpSku) || mcpSku;
                    const lookupKey = productSku || surfaceSpecificationUrl;
                    if (lookupKey) {
                        const productName = (await getProductNameBasedOnSku(lookupKey, signal)) ?? '';
                        handleProductNameChange(productName);
                    }
                }

                // 2. Resolve 3D preview availability
                try {
                    if (resolved?.source === 'lookup') {
                        setShow3DPreview(true);
                        return;
                    }

                    if (!mcpSku) {
                        setShow3DPreview(false);
                        return;
                    }

                    if (mcpSku.startsWith('PRD-')) {
                        mcpSku = await resolveProductSku(surfaceSpecificationUrl || mcpSku, signal);
                    }

                    if (productVersion === null) {
                        productVersion = await getProductCurrentVersion(mcpSku, signal);
                        if (!productVersion) {
                            setShow3DPreview(false);
                            return;
                        }
                    }

                    if (Object.keys(options).length === 0) {
                        options = parseOptionsFromSurfaceUrl(surfaceSpecificationUrl);
                    }

                    const authHeaders = await getAuthHeaders();
                    const productReference = {
                        id: mcpSku,
                        attributes: options,
                        version: productVersion,
                    };

                    const fetchPromises = TENANT_TYPE_COMBINATIONS.map(async (combo) => {
                        try {
                            const {
                                views: [view],
                            } = await getViews({
                                authorizationHeader: authHeaders.Authorization,
                                request: {
                                    reference: productReference,
                                    tenant: { id: combo.tenant, type: combo.type },
                                    previewType: ['vortex'],
                                },
                            });
                            return !!view;
                        } catch (e) {
                            return false;
                        }
                    });

                    const results = await Promise.all(fetchPromises);
                    const isAvailable = results.some((r) => r === true);

                    setShow3DPreview(isAvailable);
                } catch (e) {
                    if (signal.aborted) {
                        return;
                    }
                    console.error('Error checking 3D preview availability:', e);
                    setShow3DPreview(false);
                }
            })();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [surfaceSpecificationUrl],
    );

    useEffect(() => {
        hasPageLoadedRef.current = true;
    }, []);

    function handleProductNameChange(productName: string) {
        if (!productName) return;
        if (!initialProductNameRef.current || hasPageLoadedRef.current) {
            setProductName(productName);
        }
    }

    const onChangeSelectPanels = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setPanels(selected ?? DEFAULT_PANEL_SELECTOR_OPTION);
    };
    const onChangeSelectCulture = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        if (cultureChangeTimeoutRef.current !== null) {
            clearTimeout(cultureChangeTimeoutRef.current);
            cultureChangeTimeoutRef.current = null;
        }
        setCultureInput(selected.value);
        setCulture(selected);
        console.log('updated select');
    };
    const onChangeCulture = (value: string) => {
        setCultureInput(value);
        if (cultureChangeTimeoutRef.current !== null) {
            clearTimeout(cultureChangeTimeoutRef.current);
        }
        // Debounce so that a re-rendering request is not sent on every keystroke
        cultureChangeTimeoutRef.current = setTimeout(() => {
            setCulture({ label: value, value });
            cultureChangeTimeoutRef.current = null;
            console.log('updated text');
        }, 200);
    };

    const handleDpiThresholdChange = (dpiThreshold: string | undefined | null) => {
        if (!dpiThreshold) {
            return;
        }
        setDpiThreshold(Number.parseFloat(dpiThreshold));
    };

    const brandProfileUrlTabContent = (
        <Control>
            <Control.Title>Brand Profile URL</Control.Title>
            <CustomSelector
                selectorLabel="Brand Profile URL"
                textfieldLabel="Brand Profile URL"
                data={brandProfileUrls}
                value={brandProfileUrl}
                setValue={setBrandProfileUrl}
                validator={isUrl}
            />
        </Control>
    );

    const docUrlTabContent = (
        <Control>
            <Control.Title>Source Document</Control.Title>
            <CustomSelector
                selectorLabel="Document"
                textfieldLabel="Document URL"
                data={fullbleedSourceDocuments}
                value={sourceDocumentUrl}
                setValue={setSourceDocumentUrl}
                validator={isUrl}
            />
        </Control>
    );

    const assetJsonTabContent = (
        <Control>
            <Control.Title>Assets</Control.Title>
            <InputJsonSelector
                rows={25}
                label="Assets"
                initialValue={assets}
                onChange={setAssets}
                predefinedValues={predefinedComponents}
            />
        </Control>
    );

    const tabs = [
        {
            name: 'Document URL',
            block: docUrlTabContent,
        },
        {
            name: 'Brand Profile URL',
            block: brandProfileUrlTabContent,
        },
        {
            name: 'Assets JSON',
            block: assetJsonTabContent,
        },
    ];

    const handleTabChanged = (_: any, selectedKey: number) => {
        if (selectedKey === 0) {
            // doc URL variant
            setAssets(undefined);
            setSourceDocumentUrl(sourceDocumentUrl);
        } else {
            // asset JSON variant
            setAssets(assets);
            setSourceDocumentUrl('');
        }
    };

    return (
        <Page>
            <Page.Title>Generate</Page.Title>

            <Page.Controls>
                <Control>
                    <div className={classes.toggleRow}>
                        <Toggle on={isVersion4} onClick={() => setIsVersion4(!isVersion4)} size="sm" />
                        <span className={classes.toggleLabel}>Use V4</span>
                    </div>
                </Control>

                {isVersion4 && (
                    <Control>
                        <Control.Title>Priority</Control.Title>
                        <Select label="priority" value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} />
                    </Control>
                )}

                <Control>
                    <Control.Title>Surface Selection</Control.Title>
                    <SurfaceSelector
                        surfaceSpecificationUrl={surfaceSpecificationUrl}
                        setSurfaceSpecificationUrl={setSurfaceSpecificationUrl}
                        surfaceSpecification={surfaceSpecification}
                        setSurfaceSpecification={setSurfaceSpecification}
                        presetSurfaceUrlsFromPage={presetSurfaceUrls}
                        presetSurfaceJsonFromPage={presetSurfaces}
                    />
                </Control>

                <Control>
                    <Control.Title>Product Name</Control.Title>
                    <TextField
                        label="Product Name"
                        onChange={(e) => setProductName(e.target.value)}
                        value={productName}
                    />
                    {detectedProductCategory && detectedProductCategory !== 'default' && (
                        <div className={classes.recognizedProductCategory}>
                            <strong>Recognized Product Category:</strong>{' '}
                            <span className={classes.recognizedProductCategoryValue}>{detectedProductCategory}</span>
                        </div>
                    )}
                </Control>

                <Control>
                    <Alert
                        status={isDocumentAssetAlertInfo ? 'info' : 'danger'}
                        dismissible={false}
                        message="Provide either source document url or json with assets."
                    />

                    <TabCard tabs={tabs} onSelect={handleTabChanged} />
                </Control>

                {!isVersion4 && (
                    <Control>
                        <Control.Title>Minimum required DPI for an image</Control.Title>
                        <TextField
                            label="DPI Threshold"
                            type="number"
                            onChange={(e) => handleDpiThresholdChange(e.target.value)}
                            value={dpiThreshold}
                        />
                    </Control>
                )}

                <Control>
                    <Control.Title>Panels</Control.Title>

                    <Select
                        onChange={onChangeSelectPanels}
                        value={panels}
                        options={panelsValueOptions}
                        label="Panels"
                    />
                </Control>

                <Control>
                    <Control.Title>Culture</Control.Title>

                    {isVersion4 ? (
                        <>
                            <Select
                                onChange={onChangeSelectCulture}
                                value={
                                    V4_CULTURE_VALUE_OPTIONS.find((option) => option.value === cultureInput) ??
                                    V4_CULTURE_VALUE_OPTIONS[0]
                                }
                                options={V4_CULTURE_VALUE_OPTIONS}
                                label="Culture"
                            />
                            <TextField
                                label="Culture value"
                                onChange={(e) => onChangeCulture(e.target.value)}
                                value={cultureInput}
                            />
                        </>
                    ) : (
                        <Select
                            onChange={onChangeSelectCulture}
                            value={culture}
                            options={V3_CULTURE_VALUE_OPTIONS}
                            label="Culture"
                        />
                    )}
                </Control>

                <Control>
                    <Control.Title>Substrate Color</Control.Title>
                    <SketchPicker
                        width="65%"
                        color={substrateColor || '#FFFFFF'}
                        onChangeComplete={(e) => setSubstrateColor(e.hex)}
                    />
                </Control>

                <div className={classes.toggleRow}>
                    <Toggle
                        on={enableMulticolorLogoContrastFix}
                        onClick={() => setEnableMulticolorLogoContrastFix(!enableMulticolorLogoContrastFix)}
                        size="sm"
                        disabled={!isVersion4}
                    />
                    <span className={classes.toggleLabel}>Enable Multicolor Logo Contrast Fix</span>
                </div>
                <div className={classes.experimentalToggleRow}>
                    <div className={classes.experimentalToggleInner}>
                        <Toggle
                            on={enableMulticolorLogoOutlineContrastFix}
                            onClick={() =>
                                setEnableMulticolorLogoOutlineContrastFix(!enableMulticolorLogoOutlineContrastFix)
                            }
                            size="sm"
                            disabled={!isVersion4}
                        />
                        <Tooltip direction="right" contents="Experimental: Intended for playground testing only.">
                            <span className={classes.experimentalBadge}>Experimental</span>
                        </Tooltip>
                    </div>
                    <span>Enable Multicolor Outline Logo Contrast Fix</span>
                </div>
                <div className={classes.experimentalToggleRow}>
                    <div className={classes.experimentalToggleInner}>
                        <Toggle
                            on={enableEmbroideryLogoContrastFix}
                            onClick={() => setEnableEmbroideryLogoContrastFix(!enableEmbroideryLogoContrastFix)}
                            size="sm"
                            disabled={!isVersion4}
                        />
                        <Tooltip direction="right" contents="Experimental: Intended for playground testing only.">
                            <span className={classes.experimentalBadge}>Experimental</span>
                        </Tooltip>
                    </div>
                    <span>Enable Embroidery Logo Contrast Fix</span>
                </div>
                <div className={classes.experimentalToggleRow}>
                    <div className={classes.experimentalToggleInner}>
                        <Toggle
                            on={enableGeneratePatternDocument}
                            onClick={() => setEnableGeneratePatternDocument(!enableGeneratePatternDocument)}
                            size="sm"
                        />
                        <Tooltip direction="right" contents="Experimental: Intended for playground testing only.">
                            <span className={classes.experimentalBadge}>Experimental</span>
                        </Tooltip>
                    </div>
                    <span>Enable Generate Pattern Document</span>
                </div>
            </Page.Controls>

            <Page.Content>
                <PreviewSection
                    introduction={
                        'This is a page to test the generate endpoint. Fill in a surface and a document URL, assets or a brand profile URL to see the generate result.'
                    }
                    showIntro={!hasRequiredInput}
                    rows={PreviewRows(originRow, resultRow)}
                />
            </Page.Content>
        </Page>
    );
};
